import { Button, Col, Modal, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useProfily from 'hooks/useProfily';

import {
    IDodatekIsinData,
    IDodatekIsins,
    dokumentDodatekIsinGet,
    dokumentDodatekIsinPut
} from 'services/dokumenty/dodatek';
import { IDokJinaInfo, dokJinaInfoGet } from 'services/dokumenty/prikazy';
import {
    IDokument,
    IDokumentyPrava,
    IDokumentyPrehled,
    ISmlouvniStrany,
    dokumentSmlouvniStranyGet,
    dokumentVyjadreniApi
} from 'services/dokumenty/smlouva';
import { IPodpisData } from 'services/dokumenty/sms';
import { ETypPrevodu } from 'services/specialniSluzby/uePozadavky';

import { EPodTypDokumentu, EStavZadostiDok, ETypDokumentu, ETypZpusobPredani } from 'enums/dokumentyEnums';
import { EEvidenceCp } from 'enums/isin/evidenceCp';
import { EProfilyTyp } from 'enums/profilyTyp';

import getLanguageObject from 'utils/getLanguageObject';

import IdentifikaceStran from './IdentifikaceStran';
import RozcestnikJinaOsoba from './JinaOsobaRozcestnik';
import Osoby from './Osoby';
import Podpisy from './Podpisy';
import Predmet from './Predmet';
import Rozcestnik from './Rozcestnik';
import Admin from './dodatekPages/Admin';
import BankUcet from './dodatekPages/BankUcet';
import BezZaplaceni from './dodatekPages/BezZaplaceni';
import DodatekDetail from './dodatekPages/DodatekDetail';
import FormaCp from './dodatekPages/FormaCp';
import NavyseniDetail from './dodatekPages/NavyseniDetail';
import ProtiZaplaceni from './dodatekPages/ProtiZaplaceni';
import Shrnuti from './dodatekPages/Shrnuti';
import Blokace from './prikazyPages/Blokace';
import PrikazForma from './prikazyPages/Forma';
import PrikazJmenovita from './prikazyPages/Jmenovita';
import OmezenaPrevod from './prikazyPages/OmezenaPrevod';
import PrikazPrevod from './prikazyPages/Prevod';
import Prodlouzeni from './prikazyPages/Prodlouzeni';
import Seznam from './prikazyPages/Seznam';
import PrikazSplynuti from './prikazyPages/Splynuti';
import PrikazStepeni from './prikazyPages/Stepeni';
import PrikazVytesneni from './prikazyPages/Vytesneni';
import ZadostPosila from './prikazyPages/ZadostPosila';
import ZapisPpn from './prikazyPages/ZapisPpn';
import ZmenaUdaju from './prikazyPages/ZmenaUdaju';
import PrikazZmena from './prikazyPages/Zmenit';
import Zmenregi from './prikazyPages/Zmenregi';
import PrikazZnizeni from './prikazyPages/Znizeni';
import PrikazZruseni from './prikazyPages/Zruseni';
import ZruseniPPN from './prikazyPages/ZruseniPPN';
import ZruseniZapisuPpn from './prikazyPages/ZruseniZapisuPpn';
import OsobyJina from './prikazyPages/jineOsoby/OsobyJina';
import PodpisyJina from './prikazyPages/jineOsoby/PodpisyJina';
import VypisJinaOsoba from './prikazyPages/jineOsoby/VypisJinaOsoba';
import ZruseniJine from './prikazyPages/jineOsoby/ZruseniJine';

export const isIsinDlt = (
    isin: string,
    dokumenty: {
        selectedZadost?: IDokument;
        prehledData?: IDokumentyPrehled;
        goToSignatures?: boolean;
        dokumentyPrava?: IDokumentyPrava;
        dokumentyMenuReady?: boolean;
    }
) => {
    const isinData = (dokumenty?.dokumentyPrava?.prikazy || [])
        .filter(e => e.typprikazu === (dokumenty?.selectedZadost?.podtyp! as any))[0]
        ?.emise?.filter(e => e.isin === isin)[0]?.data;
    return isinData
        ? (JSON.parse(isinData) as IDodatekIsinData).evidencecp === EEvidenceCp.CDCPDLT
        : dokumenty?.selectedZadost?.podtyp &&
              [EPodTypDokumentu.SMLOUVADLT, EPodTypDokumentu.DODATEKDLT, EPodTypDokumentu.NAVYSENIDLT].includes(
                  dokumenty?.selectedZadost?.podtyp
              );
};
interface INovyDokument {}
export interface IPrikazyIsins {
    isin: string;
    data: string;
}

export interface IJinaOsobaData {
    osobaData: IDokJinaInfo;
    podpisData: IPodpisData;
}

const NovyDokument: React.FC<INovyDokument> = () => {
    const { confirm } = Modal;
    const { dokumenty, language, aktivniFirma } = useContext(Context);
    const { setLevel } = useProfily();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [zadostID, setZadostID] = useState<number | undefined>(dokumenty?.selectedZadost?.iddok);
    const [modalVyjadreni, setModalVyjadreni] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [signatureRedirect, setSignatureRedirect] = useState<boolean>(false);
    const [prikazyData, setPrikazyData] = useState<ISmlouvniStrany>();
    const [dodatekData, setDotatekData] = useState<IDodatekIsins>();
    const [jinaOsobaData, setJinaOsobaData] = useState<IJinaOsobaData>();
    const [signingDisabled, setSigningDisabled] = useState<boolean>(false);

    const next = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep + 1);
    };

    const prev = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep - 1);
    };

    const steps = [
        {
            title: '',
            content: <Rozcestnik nextStep={next} setZadostID={setZadostID} />
        },
        {
            title: '',
            content: <IdentifikaceStran nextStep={next} zadostID={zadostID!} signingDisabled={signingDisabled} />
        },
        ...(dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.SMLOUVADLT ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.SMLOUVA
            ? [
                  {
                      title: 'DOKUMENT_DODATEK_SHRNUTI_TITLE',
                      content: (
                          <Predmet
                              zadostID={zadostID!}
                              nextStep={next}
                              prevStep={prev}
                              isDLT={dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.SMLOUVADLT}
                          />
                      )
                  }
              ]
            : []),
        ...(dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEK ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEKDLT ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENI ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT
            ? [
                  {
                      title: '',
                      content: (
                          <BankUcet
                              nextStep={next}
                              prevStep={prev}
                              zadostID={zadostID!}
                              signingDisabled={signingDisabled}
                          />
                      )
                  },
                  {
                      title: '',
                      content:
                          dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENI ||
                          dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT ? (
                              <NavyseniDetail
                                  nextStep={next}
                                  prevStep={prev}
                                  zadostID={zadostID!}
                                  setDotatekData={setDotatekData}
                                  dodatekData={dodatekData}
                                  signingDisabled={signingDisabled}
                              />
                          ) : (
                              <DodatekDetail
                                  nextStep={next}
                                  prevStep={prev}
                                  zadostID={zadostID!}
                                  setDotatekData={setDotatekData}
                                  dodatekData={dodatekData}
                                  signingDisabled={signingDisabled}
                              />
                          )
                  },
                  {
                      title: '',
                      content: (
                          <FormaCp
                              nextStep={next}
                              prevStep={prev}
                              zadostID={zadostID!}
                              setDotatekData={setDotatekData}
                              dodatekData={dodatekData}
                              signingDisabled={signingDisabled}
                              isDlt={
                                  dokumenty?.selectedZadost?.podtyp &&
                                  [
                                      EPodTypDokumentu.SMLOUVADLT,
                                      EPodTypDokumentu.DODATEKDLT,
                                      EPodTypDokumentu.NAVYSENIDLT
                                  ].includes(dokumenty?.selectedZadost?.podtyp)
                              }
                              isCdcp={
                                  !!dodatekData?.data &&
                                  (dodatekData?.data as IDodatekIsinData).evidencecp === EEvidenceCp.CDCP
                              }
                          />
                      )
                  },
                  ...(dodatekData?.typprevodu === ETypPrevodu.BEZZAPLACENI &&
                  dodatekData.zpuspredfp === ETypZpusobPredani.CDCP // &&
                      ? // (typeof dodatekData.dodatekdvp === 'boolean' ? dodatekData.dodatekdvp : true)
                        [
                            {
                                title: '',
                                content: (
                                    <BezZaplaceni
                                        nextStep={next}
                                        prevStep={prev}
                                        zadostID={zadostID!}
                                        dodatekData={dodatekData}
                                        signingDisabled={signingDisabled}
                                    />
                                )
                            }
                        ]
                      : []),
                  ...((dodatekData?.typprevodu === ETypPrevodu.PROTIZAPLACENI ||
                      dodatekData?.typprevodu === ETypPrevodu.PROTIABEZZAPL) &&
                  dodatekData.zpuspredvp !== ETypZpusobPredani.PARSYST
                      ? [
                            {
                                title: '',
                                content: (
                                    <ProtiZaplaceni
                                        dodatekData={dodatekData}
                                        nextStep={next}
                                        prevStep={prev}
                                        zadostID={zadostID!}
                                        signingDisabled={signingDisabled}
                                        isDLT={
                                            dokumenty?.selectedZadost?.podtyp &&
                                            [
                                                EPodTypDokumentu.SMLOUVADLT,
                                                EPodTypDokumentu.DODATEKDLT,
                                                EPodTypDokumentu.NAVYSENIDLT
                                            ].includes(dokumenty?.selectedZadost?.podtyp)
                                        }
                                    />
                                )
                            }
                        ]
                      : [])
              ]
            : []),
        ...(dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEK ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEKDLT ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENI ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT
            ? [
                  {
                      title: '',
                      content: (
                          <Admin
                              zadostID={zadostID!}
                              nextStep={next}
                              prevStep={prev}
                              signingDisabled={signingDisabled}
                              dodatekData={dodatekData}
                              setDotatekData={setDotatekData}
                              mode={
                                  dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENI ||
                                  dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT
                                      ? 'navyseni'
                                      : 'dokument'
                              }
                          />
                      )
                  }
              ]
            : []),
        {
            title: '',
            content: (
                <Osoby
                    nextStep={next}
                    prevStep={prev}
                    zadostID={zadostID!}
                    isDodatek={
                        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEK ||
                        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEKDLT
                    }
                />
            )
        },
        ...(dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEK ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEKDLT ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENI ||
        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT
            ? [
                  {
                      title: 'DOKUMENT_DODATEK_SHRNUTI_TITLE',
                      content: (
                          <Shrnuti
                              zadostID={zadostID!}
                              nextStep={next}
                              prevStep={prev}
                              isDLT={
                                  dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEKDLT ||
                                  dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT
                              }
                              dodatekData={dodatekData}
                              setSigningDisabled={setSigningDisabled}
                              isNavyseni={
                                  dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENI ||
                                  dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT
                              }
                          />
                      )
                  }
              ]
            : []),
        {
            title: 'DOKUMENT_DODATEK_SHRNUTI_TITLE',
            content: (
                <Podpisy
                    prevStep={prev}
                    zadostID={zadostID!}
                    showPreControl={
                        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEK ||
                        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.DODATEKDLT ||
                        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENI ||
                        dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.NAVYSENIDLT
                    }
                />
            )
        }
    ];
    const prikazSteps = [
        {
            title: '',
            content: <Rozcestnik nextStep={next} setZadostID={setZadostID} />
        },
        ...(dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZMENY
            ? [
                  {
                      title: '',
                      content: <PrikazZmena nextStep={next} zadostID={zadostID!} prikazyData={prikazyData!} />
                  }
              ]
            : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.SNIZENI
              ? [
                    {
                        title: '',
                        content: <PrikazZnizeni nextStep={next} zadostID={zadostID!} prikazyData={prikazyData!} />
                    }
                ]
              : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.SPLYNUTI
                ? [
                      {
                          title: '',
                          content: <PrikazSplynuti nextStep={next} zadostID={zadostID!} prikazyData={prikazyData!} />
                      }
                  ]
                : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.FORMA
                  ? [
                        {
                            title: '',
                            content: <PrikazForma nextStep={next} zadostID={zadostID!} prikazyData={prikazyData!} />
                        }
                    ]
                  : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.VYTESNENI
                    ? [
                          {
                              title: '',
                              content: (
                                  <PrikazVytesneni nextStep={next} zadostID={zadostID!} prikazyData={prikazyData!} />
                              )
                          }
                      ]
                    : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZRUSENI
                      ? [
                            {
                                title: '',
                                content: (
                                    <PrikazZruseni nextStep={next} zadostID={zadostID!} prikazyData={prikazyData!} />
                                )
                            }
                        ]
                      : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.PREVODITELNOST
                        ? [
                              {
                                  title: '',
                                  content: (
                                      <PrikazPrevod nextStep={next} zadostID={zadostID!} prikazyData={prikazyData!} />
                                  )
                              }
                          ]
                        : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.STEPENI
                          ? [
                                {
                                    title: '',
                                    content: (
                                        <PrikazStepeni
                                            nextStep={next}
                                            zadostID={zadostID!}
                                            prikazyData={prikazyData!}
                                        />
                                    )
                                }
                            ]
                          : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.JMENOVITA
                            ? [
                                  {
                                      title: '',
                                      content: (
                                          <PrikazJmenovita
                                              nextStep={next}
                                              zadostID={zadostID!}
                                              prikazyData={prikazyData!}
                                          />
                                      )
                                  }
                              ]
                            : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZRUSPPN
                              ? [
                                    {
                                        title: '',
                                        content: (
                                            <ZruseniPPN
                                                nextStep={next}
                                                zadostID={zadostID!}
                                                prikazyData={prikazyData!}
                                            />
                                        )
                                    }
                                ]
                              : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.PRODLUZ
                                ? [
                                      {
                                          title: '',
                                          content: (
                                              <Prodlouzeni
                                                  nextStep={next}
                                                  zadostID={zadostID!}
                                                  prikazyData={prikazyData!}
                                              />
                                          )
                                      }
                                  ]
                                : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZMENEMISI
                                  ? [
                                        {
                                            title: '',
                                            content: (
                                                <ZmenaUdaju
                                                    nextStep={next}
                                                    zadostID={zadostID!}
                                                    prikazyData={prikazyData!}
                                                />
                                            )
                                        }
                                    ]
                                  : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZAPISBLOK
                                    ? [
                                          {
                                              title: '',
                                              content: (
                                                  <Blokace
                                                      nextStep={next}
                                                      zadostID={zadostID!}
                                                      prikazyData={prikazyData!}
                                                  />
                                              )
                                          }
                                      ]
                                    : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.PREVODZPET
                                      ? [
                                            {
                                                title: '',
                                                content: (
                                                    <ZruseniZapisuPpn zadostID={zadostID!} prikazyData={prikazyData!} />
                                                )
                                            }
                                        ]
                                      : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.SEZUPIS
                                        ? [
                                              {
                                                  title: '',
                                                  content: <Seznam zadostID={zadostID!} prikazyData={prikazyData!} />
                                              }
                                          ]
                                        : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZMENREGI
                                          ? [
                                                {
                                                    title: '',
                                                    content: (
                                                        <Zmenregi zadostID={zadostID!} prikazyData={prikazyData!} />
                                                    )
                                                }
                                            ]
                                          : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.PREVODNASL
                                            ? [
                                                  {
                                                      title: '',
                                                      content: (
                                                          <ZapisPpn zadostID={zadostID!} prikazyData={prikazyData!} />
                                                      )
                                                  }
                                              ]
                                            : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.OMEZPREVOD
                                              ? [
                                                    {
                                                        title: '',
                                                        content: (
                                                            <OmezenaPrevod
                                                                zadostID={zadostID!}
                                                                prikazyData={prikazyData!}
                                                            />
                                                        )
                                                    }
                                                ]
                                              : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ADMINZMENY ||
                                                  dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ADMINZRUS
                                                ? [
                                                      {
                                                          title: '',
                                                          content: (
                                                              <Admin
                                                                  zadostID={zadostID!}
                                                                  nextStep={next}
                                                                  prevStep={prev}
                                                                  signingDisabled={signingDisabled}
                                                                  prikazyData={prikazyData!}
                                                                  setDotatekData={setDotatekData}
                                                                  mode={
                                                                      dokumenty?.selectedZadost?.podtyp ===
                                                                      EPodTypDokumentu.ADMINZMENY
                                                                          ? 'prikazzmena'
                                                                          : 'prikazruseni'
                                                                  }
                                                              />
                                                          )
                                                      }
                                                  ]
                                                : []),
        {
            title: '',
            content: <ZadostPosila prevStep={prev} zadostID={zadostID!} />
        }
    ];
    const jinaOsobaSteps = [
        {
            title: '',
            content: (
                <RozcestnikJinaOsoba
                    nextStep={next}
                    setZadostID={setZadostID}
                    zadostID={zadostID}
                    setJinaOsobaData={setJinaOsobaData}
                    jinaOsobaData={jinaOsobaData}
                />
            )
        },
        ...(dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.EMISELIKV
            ? [
                  {
                      title: '',
                      content: (
                          <ZruseniJine
                              nextStep={next}
                              zadostID={zadostID!}
                              jinaOsobaData={jinaOsobaData!}
                              type="likvidace"
                          />
                      )
                  }
              ]
            : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.EMISEFUZE
              ? [
                    {
                        title: '',
                        content: (
                            <ZruseniJine
                                nextStep={next}
                                zadostID={zadostID!}
                                jinaOsobaData={jinaOsobaData!}
                                type="fuze"
                            />
                        )
                    }
                ]
              : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZMENFUZE
                ? [
                      {
                          title: '',
                          content: (
                              <ZruseniJine
                                  nextStep={next}
                                  zadostID={zadostID!}
                                  jinaOsobaData={jinaOsobaData!}
                                  type="prevzeti"
                              />
                          )
                      }
                  ]
                : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.EMISELIST
                  ? [
                        {
                            title: '',
                            content: (
                                <ZruseniJine
                                    nextStep={next}
                                    zadostID={zadostID!}
                                    jinaOsobaData={jinaOsobaData!}
                                    type="list"
                                />
                            )
                        }
                    ]
                  : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.ZMENJINA
                    ? [
                          {
                              title: '',
                              content: (
                                  <ZruseniJine
                                      nextStep={next}
                                      zadostID={zadostID!}
                                      jinaOsobaData={jinaOsobaData!}
                                      type="jina"
                                  />
                              )
                          }
                      ]
                    : dokumenty?.selectedZadost?.podtyp === EPodTypDokumentu.VYPIS
                      ? [
                            {
                                title: '',
                                content: (
                                    <VypisJinaOsoba
                                        nextStep={next}
                                        zadostID={zadostID!}
                                        jinaOsobaData={jinaOsobaData!}
                                    />
                                )
                            }
                        ]
                      : []),
        {
            title: '',
            content: <OsobyJina prevStep={prev} nextStep={next} zadostID={zadostID!} />
        },
        {
            title: '',
            content: <PodpisyJina prevStep={prev} zadostID={zadostID!} jinaOsobaData={jinaOsobaData!} />
        }
    ];

    useEffect(() => {
        signatureRedirect &&
            setCurrentStep((aktivniFirma?.typ === EProfilyTyp.JINAOSOBA ? jinaOsobaSteps.length : steps.length) - 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signatureRedirect]);

    useEffect(() => {
        if (
            dokumenty?.selectedZadost?.typ === ETypDokumentu.PRIKAZY &&
            zadostID &&
            aktivniFirma?.typ !== EProfilyTyp.JINAOSOBA
        ) {
            setLoading(true);
            dokumentSmlouvniStranyGet(zadostID)
                .then(resp => {
                    setPrikazyData(resp);
                    setCurrentStep(1);
                })
                .finally(() => setLoading(false));
        }
        if (aktivniFirma?.typ === EProfilyTyp.JINAOSOBA && zadostID) {
            setLoading(true);
            dokJinaInfoGet(zadostID)
                .then(resp => {
                    setJinaOsobaData(pv => {
                        return {
                            ...pv!,
                            osobaData: resp
                        };
                    });
                    !dokumenty?.goToSignatures && setCurrentStep(1);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    useEffect(() => {
        if (zadostID && dokumenty?.selectedZadost?.typ !== ETypDokumentu.PRIKAZY) {
            setLoading(true);
            dokumentDodatekIsinGet(zadostID)
                .then(resp => {
                    const respFormated = {
                        ...resp,
                        data: JSON.parse(resp.data as any)
                    };
                    setDotatekData(respFormated);
                    if (resp.potvrzeno) {
                        setSigningDisabled(true);
                        setSignatureRedirect(true);
                    } else if (!resp.potvrzeno && dokumenty?.selectedZadost?.iddok && !dokumenty.goToSignatures) {
                        dokumenty?.selectedZadost && setCurrentStep(1); // rozpracovana ziadost => prejde na dalsiu stranu
                        // presmerovanie po banksign
                    }
                })
                .catch(() => {
                    if (dokumenty?.selectedZadost?.iddok && !dokumenty.goToSignatures) {
                        dokumenty?.selectedZadost && setCurrentStep(1);
                    }
                })
                .finally(() => setLoading(false));
        }
        if (
            dokumenty &&
            (dokumenty.selectedZadost?.stav === EStavZadostiDok.DOPL ||
                dokumenty.selectedZadost?.stav === EStavZadostiDok.KDOPLNENI)
        ) {
            // zobrazi vyjadrenie cdcp v modale
            setLoading(true);
            dokumentVyjadreniApi(dokumenty.selectedZadost?.iddok, aktivniFirma?.typ === EProfilyTyp.JINAOSOBA)
                .then(data => setModalVyjadreni(data.vyjadreni))
                .finally(() => setLoading(false));
        }
        if (dokumenty?.goToSignatures) {
            setLevel('dokumenty', {
                ...dokumenty,
                goToSignatures: false
            });
            setCurrentStep((aktivniFirma?.typ === EProfilyTyp.JINAOSOBA ? jinaOsobaSteps.length : steps.length) - 1);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LoadingWrapper loading={loading}>
            {signingDisabled && steps.length - 2 > currentStep && (
                <Button
                    style={{
                        top: '30%',
                        right: 10,
                        position: 'fixed',
                        zIndex: 999
                    }}
                    type="primary"
                    onClick={() =>
                        confirm({
                            title: (
                                <TranslateText>
                                    {aktivniFirma?.typ === EProfilyTyp.JINAOSOBA
                                        ? 'DOKUMENTY_OSOBY_ZMENIT_JINA'
                                        : 'DOKUMENTY_OSOBY_ZMENIT'}
                                </TranslateText>
                            ),
                            cancelText: <TranslateText>NE</TranslateText>,
                            okText: <TranslateText>ANO</TranslateText>,
                            onOk() {
                                setLoading(true);
                                dokumentDodatekIsinPut(zadostID!, {
                                    ...dodatekData!,
                                    potvrzeno: false
                                })
                                    .then(() => {
                                        setSigningDisabled(false);
                                        setDotatekData({
                                            ...dodatekData!,
                                            potvrzeno: false
                                        });
                                    })
                                    .finally(() => setLoading(false));
                            }
                        })
                    }
                >
                    <TranslateText>POVOLIT_EDITACI</TranslateText>
                </Button>
            )}
            <Title
                level={3}
                text={`${
                    getLanguageObject(language)[
                        dokumenty?.selectedZadost?.typ === ETypDokumentu.PRIKAZY
                            ? 'DOKUMENTY_PRIKAZ_' + dokumenty?.selectedZadost.podtyp
                            : dokumenty?.selectedZadost?.podtyp
                              ? 'TYP_SMLOUVY_' + dokumenty?.selectedZadost?.podtyp
                              : 'NOVY_DOKUMNET'
                    ]
                }${
                    dokumenty?.selectedZadost?.druhcp
                        ? ' (' +
                          getLanguageObject(language)['ISIN_EMISE_DRUH_' + dokumenty?.selectedZadost?.druhcp] +
                          ')'
                        : ''
                }`}
            />
            <Title
                level={4}
                text={
                    dokumenty?.selectedZadost?.typ === ETypDokumentu.PRIKAZY
                        ? prikazSteps[currentStep]
                            ? prikazSteps[currentStep].title
                            : ''
                        : steps[currentStep]
                          ? steps[currentStep].title
                          : ''
                }
            />
            <Row gutter={8}>
                <Col span={24}>
                    {aktivniFirma?.typ === EProfilyTyp.JINAOSOBA
                        ? jinaOsobaSteps[currentStep] // && dokumenty?.selectedZadost?.typ === ETypDokumentu.PRIKAZY
                            ? jinaOsobaSteps[currentStep].content
                            : ''
                        : dokumenty?.selectedZadost?.typ === ETypDokumentu.PRIKAZY
                          ? prikazSteps[currentStep]
                              ? prikazSteps[currentStep].content
                              : ''
                          : steps[currentStep]
                            ? steps[currentStep].content
                            : ''}
                </Col>
            </Row>
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={!!modalVyjadreni}
                onCancel={() => {
                    setModalVyjadreni('');
                }}
                afterClose={() => {
                    setModalVyjadreni('');
                }}
                footer={null}
            >
                <Row>
                    <Paragraph text={modalVyjadreni} />
                </Row>
                <Row className="button-row">
                    <ButtonComponent
                        buttontext="ZAVRIT"
                        onClick={() => {
                            setModalVyjadreni('');
                        }}
                        id="emitentPrehledZavritBtn"
                    />
                </Row>
            </Modal>
        </LoadingWrapper>
    );
};

export default NovyDokument;
